import React from 'react';
import styled from 'styled-components';
import { Card } from '../../../components/Layout';
import { Border, Breakpoints, Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import type { KeyInsight } from '../models/assessmentReportView';

const KeyInsightsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Grid._5};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: ${Breakpoints.screen_lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const InsightCard = styled(Card)`
  flex-direction: column;
  padding: ${Grid._5};
  cursor: default;

  &:hover:not(.disabled) {
    cursor: default;
    box-shadow: ${Shadow.small};

    h4 {
      text-decoration: none;
    }
  }
`;

const InsightCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Grid._3};
  margin-bottom: ${Grid._4};
`;

const InsightIcon = styled.div<{ $background: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${Grid._7};
  height: ${Grid._7};
  border-radius: 50%;
  background: ${props => props.$background};
  color: ${Color.white};
  font-size: ${Type.Scale._4};
`;

const InsightCardTitle = styled.span`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.medium};
`;

const InsightMetric = styled.p`
  margin: 0 0 ${Grid._4};
  font-size: ${Type.Scale._6};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  line-height: ${Type.Leading.normal};
`;

const MetricScoreRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${Grid._1};
  margin: 0 0 ${Grid._4};
`;

const MetricScoreValue = styled.span`
  font-size: ${Type.Scale._6};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  line-height: 1;
`;

const MetricScoreOutOf = styled.span`
  font-size: ${Type.Scale._5};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
  line-height: 1;
  padding-bottom: ${Grid._1};
`;

const InsightMetricHighlight = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Grid._3};
  margin-bottom: ${Grid._4};
  padding: ${Grid._4};
  background: ${Color.Gray._10};
  border-radius: ${Border.radius};
`;

const InsightMetricLabel = styled.span`
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  line-height: ${Type.Leading.tall};
`;

const InsightMetricValue = styled.span`
  font-size: ${Type.Scale._6};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  white-space: nowrap;
`;

const InsightDescription = styled.p`
  margin: 0;
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
  line-height: ${Type.Leading.taller};
`;

const InsightMetricDisplay = ({ insight }: { insight: KeyInsight }) => {
  if (insight.highlight) {
    return (
      <InsightMetricHighlight>
        <InsightMetricLabel>{insight.highlight.label}</InsightMetricLabel>
        <InsightMetricValue>{insight.highlight.value}</InsightMetricValue>
      </InsightMetricHighlight>
    );
  }

  if (insight.scoreMetric) {
    const maxScore = insight.scoreMetric.maxScore ?? 5;
    return (
      <MetricScoreRow>
        <MetricScoreValue>{insight.scoreMetric.score.toFixed(2)}</MetricScoreValue>
        <MetricScoreOutOf>/{maxScore}</MetricScoreOutOf>
      </MetricScoreRow>
    );
  }

  if (insight.metric) {
    return <InsightMetric>{insight.metric}</InsightMetric>;
  }

  return null;
};

const InsightCardContent = ({ insight }: { insight: KeyInsight }) => (
  <InsightCard>
    <InsightCardHeader>
      <InsightIcon $background={insight.iconBackground}>
        <i className={insight.icon} aria-hidden="true" />
      </InsightIcon>
      <InsightCardTitle>{insight.title}</InsightCardTitle>
    </InsightCardHeader>
    <InsightMetricDisplay insight={insight} />
    <InsightDescription>{insight.description}</InsightDescription>
  </InsightCard>
);

type AssessmentReportKeyInsightsProps = {
  insights: KeyInsight[];
};

export const AssessmentReportKeyInsights = ({ insights }: AssessmentReportKeyInsightsProps) => (
  <KeyInsightsGrid>
    {insights.map(insight => (
      <InsightCardContent key={insight.title} insight={insight} />
    ))}
  </KeyInsightsGrid>
);

export default AssessmentReportKeyInsights;
